/* ============================================================
   MRF Card Finder Quiz — Vanilla JS
   Reads data from mrf-quiz-data.json + live /products/{handle}.js
   Persists state in localStorage, adds to cart via /cart/add.js
   Fires Klaviyo + GA4 events
   ============================================================ */

(function () {
  'use strict';

  const STATE_KEY = 'mrf_quiz_state';
  const SESSION_START_KEY = 'mrf_quiz_session_start';
  const BLADE_CARDS = new Set(['u3', 'u5', 'adventur', 'bush', 'tatu', 'art_knife']);

  const STATUS_INFO = {
    ok: { label: 'In stock', cls: 'ok' },
    low: { label: 'Low stock', cls: 'low' },
    preorder: { label: 'Pre-order', cls: 'preorder' },
    oos: { label: 'Out of stock', cls: 'oos' }
  };

  // ============================================================
  // Live data layer — fetches /products/{handle}.js for current price + stock
  // ============================================================

  const liveDataCache = new Map();

  async function fetchLiveProduct(handle) {
    if (!handle) return null;
    if (liveDataCache.has(handle)) return liveDataCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      liveDataCache.set(handle, data);
      return data;
    } catch (e) {
      console.warn('[MRFQuiz] live product fetch failed for', handle, e);
      return null;
    }
  }

  function liveVariant(product, variantId) {
    if (!product || !variantId) return null;
    return product.variants.find(v => v.id === Number(variantId));
  }

  function priceFromCents(cents) {
    return (Number(cents) / 100).toFixed(cents % 100 === 0 ? 0 : 2);
  }

  function variantStatus(variant, isPreorder) {
    if (!variant) return 'oos';
    if (isPreorder) return 'preorder';
    if (!variant.available || variant.inventory_quantity === 0) return 'oos';
    if (variant.inventory_quantity > 0 && variant.inventory_quantity <= 3) return 'low';
    return 'ok';
  }

  // ============================================================
  // Quiz class
  // ============================================================

  class MRFQuiz {
    constructor(rootEl) {
      this.root = rootEl;
      this.sectionId = rootEl.dataset.sectionId || 'main';
      this.contentEl = rootEl.querySelector('[data-quiz-content]');
      this.progressEl = rootEl.querySelector('[data-quiz-progress]');
      this.progressFillEl = rootEl.querySelector('[data-quiz-progress-fill]');
      this.stepLabelEl = rootEl.querySelector('[data-quiz-step-label]');
      this.backBtn = rootEl.querySelector('[data-action="back"]');
      this.resetBtn = rootEl.querySelector('[data-action="reset"]');
      this.entryPoint = rootEl.dataset.entryPoint || 'page';
      this.config = (window.MRFQuizConfig || {});
      this.data = null;
      this.state = this.loadState() || this.defaultState();
      this.email = '';
      this.emailDone = false;
      this.cartLoading = false;
      this.cartError = null;

      this._init();
    }

    async _init() {
      this._renderLoading();
      try {
        const res = await fetch(this.config.dataUrl, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`Data fetch HTTP ${res.status}`);
        this.data = await res.json();
      } catch (e) {
        console.error('[MRFQuiz] failed to load data', e);
        this._renderError('Failed to load quiz data. Please refresh the page.');
        return;
      }

      this._attachHeaderListeners();
      this._setSessionStart();
      this.render();
    }

    _setSessionStart() {
      if (this.state.step <= -1 && !sessionStorage.getItem(SESSION_START_KEY)) {
        sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
      }
    }

    _sessionDuration() {
      const t = Number(sessionStorage.getItem(SESSION_START_KEY) || 0);
      return t ? Math.round((Date.now() - t) / 1000) : 0;
    }

    defaultState() {
      return {
        step: -1,
        blade: null,
        q2: null,
        q3: null,
        q4_purpose: null,
        q4_material: null,
        shortcut: null,
        chosenCard: null,
        finalChoice: null
      };
    }

    saveState() {
      try { localStorage.setItem(STATE_KEY, JSON.stringify(this.state)); } catch (_) {}
    }

    loadState() {
      try {
        const raw = localStorage.getItem(STATE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return null;
        return Object.assign(this.defaultState(), parsed);
      } catch (_) { return null; }
    }

    setState(updates, options = {}) {
      this.state = Object.assign({}, this.state, updates);
      this.saveState();
      if (options.skipRender) return;
      this.render();
    }

    reset() {
      this.state = this.defaultState();
      this.email = '';
      this.emailDone = false;
      this.cartError = null;
      try { localStorage.removeItem(STATE_KEY); } catch (_) {}
      sessionStorage.removeItem(SESSION_START_KEY);
      this.trackEvent('Quiz Reset', { from_step: this.state.step });
      this._setSessionStart();
      this.render();
    }

    goBack() {
      const s = this.state;
      this.cartError = null;
      if (s.step === 5) {
        this.setState({ step: 4, chosenCard: null, finalChoice: null });
        return;
      }
      if (s.step === 4 && s.shortcut === 'max_mrf') {
        this.setState({ step: -1, shortcut: null });
        return;
      }
      if (s.step > -1) this.setState({ step: s.step - 1 });
    }

    // ============================================================
    // Logic
    // ============================================================

    getTop3() {
      const s = this.state;
      if (s.shortcut === 'max_mrf' && !(s.blade && s.q2 && s.q3)) {
        return this.data.max_mrf_shortcut.slice();
      }
      if (!s.q2 || !s.q3) return [];
      const features = s.blade === 'yes' ? this.data.lezo_features : this.data.noblade_features;
      const feature = features.find(f => f.id === s.q3);
      if (!feature) return [];
      const hero = feature.hero;
      const comboKey = `${s.q2}|${hero}`;
      const combo = this.data.combos[comboKey];
      if (!combo) return [hero];
      let [n2, n3] = combo;
      if (s.q4_purpose === 'gift') {
        const special = this.data.special_gift[comboKey];
        if (special) n3 = special;
        else {
          const generic = this.data.gift_override[hero];
          if (generic && generic !== hero && generic !== n2) n3 = generic;
        }
      }
      const result = [hero];
      if (n2 !== hero && !result.includes(n2)) result.push(n2);
      if (n3 !== hero && !result.includes(n3) && result.length < 3) result.push(n3);
      return result;
    }

    getBundlePartner(hero) {
      const pool = this.data.bundles[hero] || [];
      if (!pool.length) return null;

      // Filter to partners that are not fully OOS (preorder + low + ok all acceptable)
      const available = pool.filter(pid => {
        const c = this.data.cards[pid];
        if (!c) return false;
        const mat = c.material_options === 'aluminium' ? 'aluminium'
                  : c.material_options === 'steel_only' ? 'steel'
                  : (this.state.q4_material || 'titanium');
        const status = this.getCardStatus(pid, mat);
        return status !== 'oos';
      });

      if (!available.length) return null; // no addable partner → bundle option will be hidden

      const top3 = this.getTop3();
      const second = top3[1];
      if (second && available.includes(second)) return second;
      return available[0];
    }

    resolveMaterial(cardId, userPref) {
      const card = this.data.cards[cardId];
      if (!card) return null;
      if (card.material_options === 'steel_only') {
        return { material: 'steel', fallback: userPref === 'titanium', preferred_oos: null };
      }
      if (card.material_options === 'aluminium') {
        return { material: 'aluminium', fallback: false, preferred_oos: null };
      }
      // 'both'
      const live = liveDataCache.get(card.shopify_handle);
      const pref = userPref || 'titanium';
      const tiId = card.default_variants.titanium;
      const stId = card.default_variants.steel;
      const tiVar = live ? liveVariant(live, tiId) : null;
      const stVar = live ? liveVariant(live, stId) : null;
      const tiAvail = !live || (tiVar && tiVar.available);
      const stAvail = !live || (stVar && stVar.available);
      if (pref === 'titanium') {
        if (tiAvail) return { material: 'titanium', fallback: false, preferred_oos: null };
        if (stAvail) return { material: 'steel', fallback: true, preferred_oos: 'titanium' };
        return { material: 'titanium', fallback: false, preferred_oos: 'both' };
      }
      if (stAvail) return { material: 'steel', fallback: false, preferred_oos: null };
      if (tiAvail) return { material: 'titanium', fallback: true, preferred_oos: 'steel' };
      return { material: 'steel', fallback: false, preferred_oos: 'both' };
    }

    getVariantId(cardId, material) {
      const card = this.data.cards[cardId];
      if (!card) return null;
      return card.default_variants[material] || null;
    }

    getPrice(cardId, material) {
      const card = this.data.cards[cardId];
      if (!card) return 0;
      const live = liveDataCache.get(card.shopify_handle);
      const variantId = card.default_variants[material];
      if (live && variantId) {
        const variant = liveVariant(live, variantId);
        if (variant) return Number(priceFromCents(variant.price));
      }
      return card.fallback_price[material] || 0;
    }

    getCardStatus(cardId, material) {
      const card = this.data.cards[cardId];
      if (!card) return 'oos';
      const live = liveDataCache.get(card.shopify_handle);
      const variantId = card.default_variants[material];
      if (!live || !variantId) return card.preorder ? 'preorder' : 'ok';
      const variant = liveVariant(live, variantId);
      return variantStatus(variant, card.preorder);
    }

    cardImage(cardId) {
      const card = this.data.cards[cardId];
      if (!card) return '';
      const live = liveDataCache.get(card.shopify_handle);
      if (live && live.featured_image) return live.featured_image;
      return card.image_url || '';
    }

    // Preload live data for the cards we are about to display
    async _preloadCards(cardIds) {
      const handles = cardIds
        .map(id => this.data.cards[id]?.shopify_handle)
        .filter(Boolean);
      await Promise.all(handles.map(h => fetchLiveProduct(h)));
    }

    // Compact summary of a card — used to enrich analytics events for downstream
    // personalization (Klaviyo email templates etc.)
    _cardSummary(cardId) {
      const card = cardId ? this.data.cards[cardId] : null;
      if (!card) return {};
      const userMat = this.state.q4_material || 'titanium';
      const mat = card.material_options === 'aluminium' ? 'aluminium'
                : card.material_options === 'steel_only' ? 'steel'
                : userMat;
      const shopDomain = (this.config.shopDomain || 'mrf.tools').replace(/^https?:\/\//, '');
      return {
        hero_name: card.name,
        hero_handle: card.shopify_handle,
        hero_image: card.image_url,
        hero_price: this.getPrice(cardId, mat),
        hero_url: card.shopify_handle ? `https://${shopDomain}/products/${card.shopify_handle}` : null,
        hero_has_blade: !!card.has_blade,
        hero_material_resolved: mat
      };
    }

    // ============================================================
    // Render — dispatch
    // ============================================================

    _renderLoading() {
      if (!this.contentEl) return;
      this.contentEl.innerHTML = `<div class="mrf-quiz__screen" style="padding: 60px 20px; text-align: center; color: var(--mrfq-text-dim);">Loading…</div>`;
    }

    _renderError(msg) {
      if (!this.contentEl) return;
      this.contentEl.innerHTML = `<div class="mrf-quiz__screen" style="padding: 40px 20px; text-align: center; color: var(--mrfq-low);">${this._esc(msg)}</div>`;
    }

    render() {
      const step = this.state.step;

      // Set step class on root for CSS targeting (e.g. orange intro screen)
      if (this.root) {
        Array.from(this.root.classList).forEach(c => {
          if (c.startsWith('is-step-')) this.root.classList.remove(c);
        });
        const stepName = step === -1 ? 'intro' : String(step);
        this.root.classList.add(`is-step-${stepName}`);
      }

      let html = '';
      switch (step) {
        case -1: html = this._renderIntro(); break;
        case 0: html = this._renderQ1(); break;
        case 1: html = this._renderQ2(); break;
        case 2: html = this._renderQ3(); break;
        case 3: html = this._renderQ4(); break;
        case 4:
          if (this.state.chosenCard) {
            html = '';
            this.setState({ step: 5 }, { skipRender: true });
          } else {
            html = this._renderResults();
          }
          break;
        case 5: html = this._renderFinal(); break;
        default: html = this._renderIntro();
      }
      if (this.contentEl) this.contentEl.innerHTML = html;
      this._updateProgress();
      this._updateBackBtn();
      this._attachDynamicListeners();

      // Preload live data when entering results / final step
      // Only fire if there's something not yet cached — prevents infinite re-render loop
      if (step === 4 || step === 5) {
        const cardIds = this.getTop3();
        if (this.state.chosenCard && !cardIds.includes(this.state.chosenCard)) cardIds.push(this.state.chosenCard);
        const partner = this.state.chosenCard ? this.getBundlePartner(this.state.chosenCard) : null;
        if (partner) cardIds.push(partner);
        const handles = cardIds.map(id => this.data.cards[id]?.shopify_handle).filter(Boolean);
        const needsLoad = handles.some(h => !liveDataCache.has(h));
        if (needsLoad) {
          this._preloadCards(cardIds).then(() => {
            // Re-render once live data arrives to refresh prices/stock
            if (this.state.step === step) this.render();
          });
        }
      }
    }

    _updateProgress() {
      const step = this.state.step;
      if (!this.progressEl) return;
      if (step < 0) {
        this.progressEl.hidden = true;
        if (this.stepLabelEl) this.stepLabelEl.hidden = true;
        return;
      }
      this.progressEl.hidden = false;
      if (this.stepLabelEl) {
        this.stepLabelEl.hidden = false;
        this.stepLabelEl.textContent =
          step < 4 ? `Question ${step + 1} of 4` :
          step === 4 ? 'Your results' : 'Almost done';
      }
      const pct = Math.min(((step + 2) / 6) * 100, 100);
      if (this.progressFillEl) this.progressFillEl.style.width = pct + '%';
    }

    _updateBackBtn() {
      if (!this.backBtn) return;
      const s = this.state.step;
      this.backBtn.hidden = (s <= -1 || s >= 6);
    }

    // ============================================================
    // Screen: intro
    // ============================================================

    _renderIntro() {
      const c = this.data.ui_copy;
      return `
        <div class="mrf-quiz__screen">
          <div class="mrf-quiz__intro-hero">
            <div class="mrf-quiz__intro-title">${this._esc(c.intro_title)}</div>
            <div class="mrf-quiz__intro-sub">${this._esc(c.intro_sub)}</div>
          </div>
          <button class="mrf-quiz__primary-big" data-action="start-quiz">
            ${this._esc(c.cta_primary)} <span aria-hidden="true">→</span>
          </button>
          <div class="mrf-quiz__divider"><span class="mrf-quiz__divider-label">OR</span></div>
          <button class="mrf-quiz__shortcut-btn" data-action="shortcut-max">
            <span class="mrf-quiz__shortcut-icon">✨</span>
            <span class="mrf-quiz__shortcut-text">
              <span class="mrf-quiz__shortcut-label">${this._esc(c.cta_shortcut)}</span>
              <span class="mrf-quiz__shortcut-sub">${this._esc(c.cta_shortcut_sub)}</span>
            </span>
            <span class="mrf-quiz__chevron" aria-hidden="true">›</span>
          </button>
        </div>
      `;
    }

    // ============================================================
    // Screen: Q1 — blade?
    // ============================================================

    _renderQ1() {
      return `
        <div class="mrf-quiz__screen">
          <div class="mrf-quiz__question">
            <h2 class="mrf-quiz__question-label">Do you need a blade — or rather not?</h2>
            <p class="mrf-quiz__question-sub">It's either one of your main tools — or something that gets in the way</p>
          </div>
          <button class="mrf-quiz__blade-option" data-action="set-blade" data-value="yes">
            <span class="mrf-quiz__blade-icon">🔪</span>
            <span class="mrf-quiz__blade-content">
              <span class="mrf-quiz__blade-title">Yes. Blade — my main tool</span>
              <span class="mrf-quiz__blade-pros">
                <span>✓ Replaces a bulky pocket knife — all in one card</span>
                <span>✓ Open packages, cut tags, ropes, zip ties</span>
                <span>✓ Whittle wood, prep food outdoors</span>
                <span>✓ Survival — start fires, dress game</span>
                <span>✓ Self-defense in a pinch</span>
                <span class="mrf-quiz__blade-cons">✗ Won't pass TSA in carry-on</span>
              </span>
            </span>
          </button>
          <button class="mrf-quiz__blade-option" data-action="set-blade" data-value="no">
            <span class="mrf-quiz__blade-icon">🚫</span>
            <span class="mrf-quiz__blade-content">
              <span class="mrf-quiz__blade-title">No. I need to turn screws and draw</span>
              <span class="mrf-quiz__blade-pros">
                <span>✓ Draw, sketch, measure with precision</span>
                <span>✓ Turn nuts, screws, bits, hex</span>
                <span>✓ Fly with it in carry-on (TSA-safe)</span>
                <span>✓ Safe around kids, at school, in banks</span>
                <span>✓ Keep on your desk without questions</span>
                <span class="mrf-quiz__blade-cons">✗ Won't cut, slice, or chop</span>
              </span>
            </span>
          </button>
        </div>
      `;
    }

    // ============================================================
    // Screen: Q2 — where carry?
    // ============================================================

    _renderQ2() {
      const options = this.state.blade === 'yes' ? this.data.lezo_types : this.data.noblade_types;
      return `
        <div class="mrf-quiz__screen">
          <div class="mrf-quiz__question">
            <h2 class="mrf-quiz__question-label">Where will you carry it most often?</h2>
            <p class="mrf-quiz__question-sub">This defines your style of use</p>
          </div>
          <div class="mrf-quiz__list-grid">
            ${options.map(o => this._tplListOption(o, 'set-q2')).join('')}
          </div>
        </div>
      `;
    }

    // ============================================================
    // Screen: Q3 — what do you want?
    // ============================================================

    _renderQ3() {
      const options = this.state.blade === 'yes' ? this.data.lezo_features : this.data.noblade_features;
      return `
        <div class="mrf-quiz__screen">
          <div class="mrf-quiz__question">
            <h2 class="mrf-quiz__question-label">What do you want from your card?</h2>
            <p class="mrf-quiz__question-sub">Finish the sentence — pick what fits you best</p>
          </div>
          <div class="mrf-quiz__list-grid">
            ${options.map(o => this._tplListOption(o, 'set-q3')).join('')}
          </div>
        </div>
      `;
    }

    _tplListOption(opt, action) {
      return `
        <button class="mrf-quiz__list-option" data-action="${action}" data-value="${this._esc(opt.id)}">
          <span class="mrf-quiz__list-icon">${opt.icon}</span>
          <span class="mrf-quiz__list-text">
            <span class="mrf-quiz__list-label">${this._esc(opt.label)}</span>
            <span class="mrf-quiz__list-sub">${this._esc(opt.sub)}</span>
          </span>
          <span class="mrf-quiz__chevron" aria-hidden="true">›</span>
        </button>
      `;
    }

    // ============================================================
    // Screen: Q4 — two-row
    // ============================================================

    _renderQ4() {
      const s = this.state;
      const q4Complete = s.q4_purpose && s.q4_material;
      return `
        <div class="mrf-quiz__screen">
          <div class="mrf-quiz__question">
            <h2 class="mrf-quiz__question-label">Last 2 questions — pick both</h2>
            <p class="mrf-quiz__question-sub">One from each row</p>
          </div>

          <div class="mrf-quiz__q4-section-label">
            <span class="mrf-quiz__q4-number">1</span><span>For whom?</span>
          </div>
          <div class="mrf-quiz__icon-grid">
            <button class="mrf-quiz__icon-option${s.q4_purpose === 'self' ? ' is-selected' : ''}" data-action="set-q4-purpose" data-value="self">
              <span class="mrf-quiz__icon-emoji">👤</span>
              <span class="mrf-quiz__icon-label">For myself</span>
              <span class="mrf-quiz__icon-sub">Practical, daily use</span>
            </button>
            <button class="mrf-quiz__icon-option${s.q4_purpose === 'gift' ? ' is-selected' : ''}" data-action="set-q4-purpose" data-value="gift">
              <span class="mrf-quiz__icon-emoji">🎁</span>
              <span class="mrf-quiz__icon-label">A gift</span>
              <span class="mrf-quiz__icon-sub">Presentable, styled</span>
            </button>
          </div>

          <div class="mrf-quiz__q4-section-label" style="margin-top: 10px;">
            <span class="mrf-quiz__q4-number">2</span><span>What material?</span>
            <span class="mrf-quiz__q4-hint">· you can change in cart</span>
          </div>
          <button class="mrf-quiz__material-option${s.q4_material === 'steel' ? ' is-selected' : ''}" data-action="set-q4-material" data-value="steel">
            <span class="mrf-quiz__material-icon">🛡️</span>
            <span class="mrf-quiz__material-content">
              <span class="mrf-quiz__material-title">I trust Steel 440C</span>
              <span class="mrf-quiz__material-pros">
                <span>✓ Harder edge (52–54 HRC)</span>
                <span>✓ Better edge retention</span>
                <span>✓ Easier to re-sharpen</span>
                <span>✓ More affordable</span>
              </span>
            </span>
            <span class="mrf-quiz__material-price">from $24</span>
          </button>
          <button class="mrf-quiz__material-option${s.q4_material === 'titanium' ? ' is-selected' : ''}" data-action="set-q4-material" data-value="titanium">
            <span class="mrf-quiz__material-icon">💎</span>
            <span class="mrf-quiz__material-content">
              <span class="mrf-quiz__material-title">I want premium Titanium</span>
              <span class="mrf-quiz__material-pros">
                <span>✓ Aerospace Grade 5 alloy</span>
                <span>✓ Featherlight (28g / 1.3oz)</span>
                <span>✓ Rust-proof, non-magnetic</span>
                <span>✓ Premium feel & finish</span>
              </span>
            </span>
            <span class="mrf-quiz__material-price">from $38</span>
          </button>

          <button class="mrf-quiz__cta-btn" data-action="goto-results" ${q4Complete ? '' : 'disabled'}>
            See my results <span aria-hidden="true">→</span>
          </button>
          ${q4Complete ? '' : `
            <div class="mrf-quiz__q4-notice">
              <span aria-hidden="true">⚠</span>
              <span>Please pick both — ${!s.q4_purpose ? 'for whom' : 'material'}</span>
            </div>
          `}
        </div>
      `;
    }

    // ============================================================
    // Screen: Results
    // ============================================================

    _renderResults() {
      const top3 = this.getTop3();
      const s = this.state;
      const c = this.data.ui_copy;
      const cards = top3.map((cid, idx) => this._tplResultCard(cid, idx)).filter(Boolean).join('');
      const showMaxBadge = s.shortcut === 'max_mrf' && !(s.blade && s.q2 && s.q3);
      return `
        <div class="mrf-quiz__screen">
          <div class="mrf-quiz__results-hero">
            <span class="mrf-quiz__results-hero-icon">🏆</span>
            <div>
              <h2 class="mrf-quiz__question-label">${this._esc(c.results_title)}</h2>
              <p class="mrf-quiz__question-sub">${this._esc(c.results_sub)}</p>
            </div>
          </div>
          ${showMaxBadge ? '<div class="mrf-quiz__max-badge">✨ The best MRF has — Universal series</div>' : ''}
          <div class="mrf-quiz__results-list">${cards}</div>
        </div>
      `;
    }

    _tplResultCard(cardId, idx) {
      const card = this.data.cards[cardId];
      if (!card) return '';
      const mat = this.resolveMaterial(cardId, this.state.q4_material);
      const material = mat ? mat.material : 'steel';
      const price = this.getPrice(cardId, material);
      const status = this.getCardStatus(cardId, material);
      const statusInfo = STATUS_INFO[status];
      const isHero = idx === 0;
      const img = this.cardImage(cardId);
      const imgEl = img
        ? `<img class="mrf-quiz__result-image" src="${this._esc(img)}" alt="${this._esc(card.name)}" loading="lazy">`
        : `<span class="mrf-quiz__result-image">${card.emoji_fallback || '🛠'}</span>`;
      return `
        <button class="mrf-quiz__result-card${isHero ? ' is-hero' : ''}" data-action="pick-card" data-card="${this._esc(cardId)}">
          <span class="mrf-quiz__result-rank">
            ${isHero
              ? '<span class="mrf-quiz__rank-hero">🏆 YOUR PICK</span>'
              : `<span class="mrf-quiz__rank-num">#${idx + 1}</span>`}
          </span>
          ${imgEl}
          <span class="mrf-quiz__result-info">
            <span class="mrf-quiz__result-name">${this._esc(card.name)}</span>
            <span class="mrf-quiz__result-tagline">${this._esc(card.tagline)}</span>
            <span class="mrf-quiz__result-meta">
              <span class="mrf-quiz__result-price">$${price}</span>
              <span class="mrf-quiz__result-status mrf-quiz__result-status--${statusInfo.cls}">· ${this._esc(statusInfo.label)}</span>
              ${mat && mat.fallback ? `<span class="mrf-quiz__result-material-note">· in ${this._materialLabel(mat.material)}</span>` : ''}
            </span>
          </span>
          <span class="mrf-quiz__chevron" aria-hidden="true">›</span>
        </button>
      `;
    }

    // ============================================================
    // Screen: Final — solo vs bundle
    // ============================================================

    _renderFinal() {
      const s = this.state;
      const c = this.data.ui_copy;
      const cardId = s.chosenCard;
      const card = cardId ? this.data.cards[cardId] : null;
      if (!card) {
        this.setState({ step: 4 }, { skipRender: true });
        return this._renderResults();
      }
      const heroMat = this.resolveMaterial(cardId, s.q4_material);
      const heroMaterial = heroMat.material;
      const heroPrice = this.getPrice(cardId, heroMaterial);
      const heroStatus = this.getCardStatus(cardId, heroMaterial);

      const partnerId = this.getBundlePartner(cardId);
      const partner = partnerId && partnerId !== cardId ? this.data.cards[partnerId] : null;
      const partnerMat = partner ? this.resolveMaterial(partnerId, s.q4_material) : null;
      const partnerMaterial = partnerMat ? partnerMat.material : null;
      const partnerPrice = partner ? this.getPrice(partnerId, partnerMaterial) : 0;

      const sleeveValue = this.data.gift_products.leather_sleeve.value_label;
      const walletValue = this.data.gift_products.mrf_wallet.value_label;

      const ctaCopy = this._ctaCopyForStatus(card, heroStatus, s.finalChoice);
      const ctaDisabled = !s.finalChoice;

      return `
        <div class="mrf-quiz__screen">
          <div class="mrf-quiz__question">
            <h2 class="mrf-quiz__question-label">${this._esc(c.final_title)}</h2>
            <p class="mrf-quiz__question-sub">${this._esc(c.final_sub)}</p>
          </div>

          ${this._tplMaterialBanner(card, heroMat, s.q4_material)}

          <div class="mrf-quiz__final-card${s.finalChoice === 'solo' ? ' is-selected' : ''}" data-action="set-final" data-value="solo" role="button" tabindex="0">
            <div class="mrf-quiz__final-badge">Just the main card</div>
            <div class="mrf-quiz__final-row">
              ${this._tplFinalImage(cardId)}
              <div class="mrf-quiz__final-info">
                <div class="mrf-quiz__final-name">${this._esc(card.name)}</div>
                <div class="mrf-quiz__final-tagline">${this._esc(this._materialLabel(heroMaterial))} · default color</div>
                <div class="mrf-quiz__final-price">$${heroPrice}</div>
              </div>
            </div>
            <div class="mrf-quiz__gift-badge">
              <span aria-hidden="true">🎁</span>
              <span>+ Free leather sleeve (${this._esc(sleeveValue)})</span>
            </div>
          </div>

          ${partner ? this._tplBundleCard({ cardId, card, partnerId, partner, heroMaterial, partnerMaterial, partnerMat, heroPrice, partnerPrice, walletValue, isSelected: s.finalChoice === 'bundle' }) : ''}

          ${this._tplCartError()}

          <button class="mrf-quiz__cta-btn${this.cartLoading ? ' is-loading' : ''}" data-action="cart-add" ${ctaDisabled ? 'disabled' : ''}>
            ${ctaCopy}
          </button>

          ${this._tplEmailGate()}
        </div>
      `;
    }

    _tplFinalImage(cardId) {
      const img = this.cardImage(cardId);
      const card = this.data.cards[cardId];
      if (img) return `<img class="mrf-quiz__final-image" src="${this._esc(img)}" alt="${this._esc(card.name)}" loading="lazy">`;
      return `<span class="mrf-quiz__final-image">${card.emoji_fallback || '🛠'}</span>`;
    }

    _tplBundleCard(args) {
      const { cardId, card, partnerId, partner, heroMaterial, partnerMaterial, partnerMat, heroPrice, partnerPrice, walletValue, isSelected } = args;
      const sum = Number(heroPrice) + Number(partnerPrice);
      const bundlePrice = sum - 3;
      const bladeMismatch = BLADE_CARDS.has(cardId) !== BLADE_CARDS.has(partnerId);
      return `
        <div class="mrf-quiz__final-card is-bundle${isSelected ? ' is-selected' : ''}" data-action="set-final" data-value="bundle" role="button" tabindex="0">
          <div class="mrf-quiz__final-badge is-rec">⭐ FULL SYSTEM</div>
          <div class="mrf-quiz__bundle-row">
            ${this._tplFinalImage(cardId)}
            <span class="mrf-quiz__bundle-plus">+</span>
            ${this._tplFinalImage(partnerId)}
          </div>
          <div class="mrf-quiz__bundle-name">${this._esc(card.name)} + ${this._esc(partner.name)}</div>
          ${partnerMat && partnerMat.fallback ? `<div class="mrf-quiz__bundle-note">💡 ${this._esc(partner.name)} comes in ${this._esc(this._materialLabel(partnerMaterial))} — that's the right material for it</div>` : ''}
          ${bladeMismatch ? `<div class="mrf-quiz__bundle-note">💡 Companion ${BLADE_CARDS.has(partnerId) ? 'with blade' : 'without blade'} — complements your main pick</div>` : ''}
          <div class="mrf-quiz__bundle-pricing">
            <span class="mrf-quiz__bundle-price-old">$${sum.toFixed(sum % 1 === 0 ? 0 : 2)}</span>
            <span class="mrf-quiz__bundle-price-new">$${bundlePrice.toFixed(bundlePrice % 1 === 0 ? 0 : 2)}</span>
            <span class="mrf-quiz__bundle-save">save $3</span>
          </div>
          <div class="mrf-quiz__gift-badge">
            <span aria-hidden="true">🎁</span>
            <span>+ MRF Wallet (${this._esc(walletValue)}) free</span>
          </div>
        </div>
      `;
    }

    _tplMaterialBanner(card, mat, userPref) {
      if (card.material_options === 'steel_only') {
        return `<div class="mrf-quiz__material-banner">
          <span class="mrf-quiz__material-banner-icon">🛡️</span>
          <span class="mrf-quiz__material-banner-text">${this._esc(card.name)} comes only in Steel 440C — that's the right material for this design</span>
        </div>`;
      }
      if (card.material_options === 'aluminium') {
        return `<div class="mrf-quiz__material-banner">
          <span class="mrf-quiz__material-banner-icon">🎨</span>
          <span class="mrf-quiz__material-banner-text">${this._esc(card.name)} is precision-machined aluminium — pick a color in cart</span>
        </div>`;
      }
      if (mat && mat.fallback) {
        return `<div class="mrf-quiz__material-banner">
          <span class="mrf-quiz__material-banner-icon">⚠️</span>
          <span class="mrf-quiz__material-banner-text">Your ${this._esc(userPref)} pick is out of stock — we have ${this._esc(card.name)} in ${this._esc(this._materialLabel(mat.material))}</span>
        </div>`;
      }
      return '';
    }

    _ctaCopyForStatus(card, status, finalChoice) {
      if (status === 'oos') return '<span aria-hidden="true">🔔</span> Notify me when back';
      if (status === 'preorder') {
        return finalChoice === 'bundle'
          ? '<span aria-hidden="true">🚀</span> Pre-order bundle'
          : '<span aria-hidden="true">🚀</span> Pre-order now';
      }
      return `<span aria-hidden="true">🛒</span> ${finalChoice === 'bundle' ? 'Add bundle to cart' : 'Add to cart'}`;
    }

    _tplEmailGate() {
      const c = this.data.ui_copy;
      if (this.emailDone) {
        return `<div class="mrf-quiz__email-gate">
          <div class="mrf-quiz__email-success"><span aria-hidden="true">✓</span> ${this._esc(c.email_success)}</div>
        </div>`;
      }
      return `<div class="mrf-quiz__email-gate">
        <div class="mrf-quiz__email-label"><span aria-hidden="true">✉</span> ${this._esc(c.email_label)}</div>
        <div class="mrf-quiz__email-row">
          <input type="email" class="mrf-quiz__email-input" data-quiz-email placeholder="${this._esc(c.email_placeholder)}" value="${this._esc(this.email)}">
          <button class="mrf-quiz__email-btn" data-action="submit-email">${this._esc(c.email_cta)}</button>
        </div>
      </div>`;
    }

    _tplCartError() {
      const ce = this.cartError;
      if (!ce) return '';
      const labelForStatus = s => s === 'preorder' ? 'Pre-order' : s === 'low' ? 'Low stock' : 'In stock';
      const altsHtml = ce.alternatives.length
        ? `<div class="mrf-quiz__cart-error-alts">
            ${ce.alternatives.map(a => `
              <button class="mrf-quiz__cart-error-alt-btn" data-action="pick-alternative" data-card="${this._esc(a.id)}">
                <span class="mrf-quiz__cart-error-alt-name">
                  <span>${this._esc(a.name)}</span>
                  <span class="mrf-quiz__cart-error-alt-tag">${labelForStatus(a.status)}</span>
                </span>
                <span aria-hidden="true">›</span>
              </button>
            `).join('')}
          </div>`
        : '';
      const helpText = ce.alternatives.length
        ? "Try one of your other top picks:"
        : "We couldn't add this to your cart. Leave your email below — we'll let you know when it's back.";
      return `<div class="mrf-quiz__cart-error" role="alert">
        <div class="mrf-quiz__cart-error-title">
          <span aria-hidden="true">⚠</span>
          <span>${this._esc(ce.cardName)} can't be added right now</span>
        </div>
        <div class="mrf-quiz__cart-error-msg">${this._esc(helpText)}</div>
        ${altsHtml}
      </div>`;
    }

    _materialLabel(m) {
      if (m === 'titanium') return 'Titanium Grade 5';
      if (m === 'steel') return 'Steel 440C';
      if (m === 'aluminium') return 'Aluminium';
      return m;
    }

    // ============================================================
    // Listeners
    // ============================================================

    _attachHeaderListeners() {
      if (this.backBtn && !this.backBtn._mrfBound) {
        this.backBtn.addEventListener('click', () => this.goBack());
        this.backBtn._mrfBound = true;
      }
      if (this.resetBtn && !this.resetBtn._mrfBound) {
        this.resetBtn.addEventListener('click', () => {
          if (this.state.step > -1 && !confirm('Reset the quiz and start over?')) return;
          this.reset();
        });
        this.resetBtn._mrfBound = true;
      }
    }

    _attachDynamicListeners() {
      if (!this.contentEl) return;
      this.contentEl.querySelectorAll('[data-action]').forEach(el => {
        if (el._mrfBound) return;
        el._mrfBound = true;
        const action = el.dataset.action;
        const value = el.dataset.value;
        const card = el.dataset.card;
        if (action === 'start-quiz') {
          el.addEventListener('click', () => {
            this.trackEvent('Quiz Started', { entry_point: this.entryPoint });
            this.setState({ shortcut: null, step: 0 });
          });
        } else if (action === 'shortcut-max') {
          el.addEventListener('click', () => {
            this.trackEvent('Quiz Started', { entry_point: this.entryPoint, shortcut: 'max_mrf' });
            this.setState({ shortcut: 'max_mrf', q4_purpose: 'self', q4_material: 'titanium', step: 4 });
          });
        } else if (action === 'set-blade') {
          el.addEventListener('click', () => {
            this.trackEvent('Quiz Q1 Answered', { blade: value });
            this.setState({ blade: value, q2: null, q3: null, step: 1 });
          });
        } else if (action === 'set-q2') {
          el.addEventListener('click', () => {
            this.trackEvent('Quiz Q2 Answered', { q2_value: value });
            this.setState({ q2: value, q3: null, step: 2 });
          });
        } else if (action === 'set-q3') {
          el.addEventListener('click', () => {
            const features = this.state.blade === 'yes' ? this.data.lezo_features : this.data.noblade_features;
            const feature = features.find(f => f.id === value);
            this.trackEvent('Quiz Q3 Answered', { q3_value: value, hero: feature?.hero });
            this.setState({ q3: value, step: 3 });
          });
        } else if (action === 'set-q4-purpose') {
          el.addEventListener('click', () => this.setState({ q4_purpose: value }));
        } else if (action === 'set-q4-material') {
          el.addEventListener('click', () => this.setState({ q4_material: value }));
        } else if (action === 'goto-results') {
          el.addEventListener('click', () => {
            const s = this.state;
            if (!s.q4_purpose || !s.q4_material) return;
            const top3 = this.getTop3();
            this.trackEvent('Quiz Q4 Completed', { purpose: s.q4_purpose, material: s.q4_material });

            // Enriched Quiz Completed — adds hero card details so Klaviyo emails can
            // personalize without theme-side lookups.
            const hero = this._cardSummary(top3[0]);
            this.trackEvent('Quiz Completed', Object.assign({
              top3_cards: top3,
              hero: top3[0],
              material: s.q4_material,
              purpose: s.q4_purpose,
              time_taken_seconds: this._sessionDuration()
            }, hero));
            this.setState({ step: 4 });
          });
        } else if (action === 'pick-card') {
          el.addEventListener('click', () => {
            const top3 = this.getTop3();
            const pos = top3.indexOf(card) + 1;
            const summary = this._cardSummary(card);
            this.trackEvent('Card Selected', Object.assign({
              card_id: card,
              position: pos
            }, summary));
            this.setState({ chosenCard: card, step: 5 });
          });
        } else if (action === 'pick-alternative') {
          el.addEventListener('click', () => {
            this.trackEvent('Alternative Picked After OOS', { from_card: this.state.chosenCard, to_card: card });
            this.cartError = null;
            this.setState({ chosenCard: card, finalChoice: null });
          });
        } else if (action === 'set-final') {
          const handler = () => {
            const c = this.data.cards[this.state.chosenCard];
            if (!c) return;
            const mat = this.resolveMaterial(this.state.chosenCard, this.state.q4_material);
            const price = this.getPrice(this.state.chosenCard, mat.material);
            this.trackEvent(value === 'bundle' ? 'Bundle Choice' : 'Solo Choice', {
              card_id: this.state.chosenCard, material: mat.material, price
            });
            this.setState({ finalChoice: value });
          };
          el.addEventListener('click', handler);
          el.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(); }
          });
        } else if (action === 'cart-add') {
          el.addEventListener('click', () => this.handleCartAdd());
        } else if (action === 'submit-email') {
          el.addEventListener('click', () => this.handleEmailSubmit());
        }
      });

      const emailInput = this.contentEl.querySelector('[data-quiz-email]');
      if (emailInput && !emailInput._mrfBound) {
        emailInput._mrfBound = true;
        emailInput.addEventListener('input', e => { this.email = e.target.value; });
        emailInput.addEventListener('keydown', e => {
          if (e.key === 'Enter') { e.preventDefault(); this.handleEmailSubmit(); }
        });
      }
    }

    // ============================================================
    // Cart add
    // ============================================================

    async handleCartAdd() {
      const s = this.state;
      const card = this.data.cards[s.chosenCard];
      if (!card) return;
      const mat = this.resolveMaterial(s.chosenCard, s.q4_material);
      const status = this.getCardStatus(s.chosenCard, mat.material);

      // OOS path: subscribe-to-restock instead
      if (status === 'oos') {
        this.trackEvent('OOS Notify Subscribed', { card_id: s.chosenCard, material: mat.material });
        alert(`We'll notify you when ${card.name} is back in stock. Leave your email below for early access.`);
        return;
      }

      // Preorder path: add to cart anyway (per user spec — preorder = cart-able)
      const items = [];
      const heroVariantId = card.default_variants[mat.material];
      if (!heroVariantId) {
        alert('Sorry, this variant is currently unavailable. Please try a different option.');
        return;
      }
      items.push({ id: Number(heroVariantId), quantity: 1, properties: { _mrf_quiz: 'hero', _mrf_quiz_session: s.shortcut || 'flow' } });

      let partnerId = null;
      let partnerMaterial = null;
      if (s.finalChoice === 'bundle') {
        partnerId = this.getBundlePartner(s.chosenCard);
        const partner = partnerId ? this.data.cards[partnerId] : null;
        if (partner) {
          const partnerMat = this.resolveMaterial(partnerId, s.q4_material);
          partnerMaterial = partnerMat.material;
          const partnerVariantId = partner.default_variants[partnerMaterial];
          if (partnerVariantId) {
            items.push({ id: Number(partnerVariantId), quantity: 1, properties: { _mrf_quiz: 'bundle_partner' } });
          }
        }
      }

      // Free gift line items
      const giftVariantId = this._chooseGiftVariantId(s.finalChoice);
      if (giftVariantId) {
        items.push({ id: Number(giftVariantId), quantity: 1, properties: { _mrf_quiz: 'free_gift', _mrf_gift_for: s.finalChoice } });
      }

      this.cartLoading = true;
      this.render();

      try {
        const res = await fetch(this.config.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items })
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => null);
          throw new Error(errBody?.description || `Cart add HTTP ${res.status}`);
        }
        const added = await res.json();
        const totalValue = Number(this.getPrice(s.chosenCard, mat.material)) + (partnerId ? Number(this.getPrice(partnerId, partnerMaterial)) : 0) - (s.finalChoice === 'bundle' ? 3 : 0);
        this.trackEvent('Added to Cart from Quiz', {
          card_id: s.chosenCard,
          type: s.finalChoice,
          material: mat.material,
          bundle_partner: partnerId || null,
          value: Number(totalValue.toFixed(2)),
          variants: items.map(i => i.id)
        });
        // Reset quiz state on success — so reopening starts fresh, but session remains for analytics
        localStorage.removeItem(STATE_KEY);
        this.cartLoading = false;
        window.location.href = this.config.cartUrl || '/cart';
      } catch (e) {
        console.error('[MRFQuiz] cart add failed', e);
        this.cartLoading = false;

        // Build cart-error state with alternatives from top3 (skip current + OOS)
        const top3 = this.getTop3();
        const alternatives = top3
          .filter(id => id !== s.chosenCard)
          .map(id => {
            const c = this.data.cards[id];
            if (!c) return null;
            const m = c.material_options === 'aluminium' ? 'aluminium'
                    : c.material_options === 'steel_only' ? 'steel'
                    : (s.q4_material || 'titanium');
            const st = this.getCardStatus(id, m);
            if (st === 'oos') return null;
            return { id, name: c.name, status: st };
          })
          .filter(Boolean);

        this.cartError = {
          cardName: card.name,
          message: e.message || 'Unable to add to cart.',
          alternatives
        };
        this.render();
      }
    }

    _chooseGiftVariantId(finalChoice) {
      const sleeve = this.data.gift_products.leather_sleeve;
      const wallet = this.data.gift_products.mrf_wallet;
      if (finalChoice === 'bundle') {
        return wallet.gift_variant_id || null;
      }
      return sleeve.gift_variant_id || null;
    }

    async handleEmailSubmit() {
      if (!this.email || !this.email.includes('@')) {
        alert('Please enter a valid email address.');
        return;
      }
      const code = this.data.discount_codes.email_signup;
      this.trackEvent('Email Captured', {
        email: this.email,
        card_selected: this.state.chosenCard,
        material: this.state.q4_material,
        discount_code: code
      });
      // Klaviyo native identification (sets the cookie)
      try {
        if (window._learnq) {
          window._learnq.push(['identify', { $email: this.email }]);
        }
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
          window.klaviyo.identify({ email: this.email });
        }
      } catch (_) {}
      this.emailDone = true;
      this.render();
    }

    // ============================================================
    // Analytics
    // ============================================================

    trackEvent(name, props = {}) {
      const enriched = Object.assign({}, props, {
        quiz_version: this.data?.version || '1.0',
        entry_point: this.entryPoint
      });
      try {
        if (window._learnq) window._learnq.push(['track', name, enriched]);
        if (window.klaviyo && typeof window.klaviyo.track === 'function') window.klaviyo.track(name, enriched);
      } catch (_) {}
      try {
        if (window.gtag) window.gtag('event', name.replace(/\s+/g, '_').toLowerCase(), enriched);
      } catch (_) {}
      try {
        if (window.dataLayer) window.dataLayer.push(Object.assign({ event: name }, enriched));
      } catch (_) {}
      if (this.config.debugMode) console.log('[MRFQuiz] track', name, enriched);
    }

    // ============================================================
    // Util
    // ============================================================

    _esc(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  // ============================================================
  // Modal wiring (popup mode)
  // ============================================================

  const Modal = {
    open() {
      const modal = document.querySelector('[data-mrf-quiz-modal]');
      if (!modal) {
        // Fallback: nav to /pages/quiz if no modal exists on this page
        window.location.href = '/pages/quiz';
        return;
      }
      modal.setAttribute('aria-hidden', 'false');
      // Note: body scroll intentionally NOT locked — site scrolls behind the modal
      // Trigger an init if not yet done (for lazy-loading content)
      const quizRoot = modal.querySelector('.mrf-quiz');
      if (quizRoot && !quizRoot._mrfInstance) {
        quizRoot._mrfInstance = new MRFQuiz(quizRoot);
      }
      window.dispatchEvent(new CustomEvent('mrf-quiz:opened'));
    },
    close() {
      const modal = document.querySelector('[data-mrf-quiz-modal]');
      if (!modal) return;
      modal.setAttribute('aria-hidden', 'true');
      window.dispatchEvent(new CustomEvent('mrf-quiz:closed'));
    }
  };

  function wireTriggers() {
    document.querySelectorAll('[data-quiz-trigger]').forEach(el => {
      if (el._mrfTriggerBound) return;
      el._mrfTriggerBound = true;
      el.addEventListener('click', e => {
        e.preventDefault();
        Modal.open();
      });
    });

    document.querySelectorAll('[data-quiz-close]').forEach(el => {
      if (el._mrfCloseBound) return;
      el._mrfCloseBound = true;
      el.addEventListener('click', e => {
        e.preventDefault();
        Modal.close();
      });
    });

    // Global delegation: any link whose href contains #mrf-quiz / /mrf-quiz / /pages/quiz
    // opens the modal. Lets the user add buttons in the Shopify Customizer just by
    // setting button URL = "#mrf-quiz" — no code edits needed.
    if (!window._mrfAnchorDelegated) {
      window._mrfAnchorDelegated = true;
      document.addEventListener('click', e => {
        const link = e.target.closest('a');
        if (!link) return;
        const href = (link.getAttribute('href') || '').toLowerCase();
        if (!href) return;
        const isQuizLink =
          href === '#mrf-quiz' ||
          href === '#open-quiz' ||
          href === '#quiz' ||
          href.endsWith('/mrf-quiz') ||
          href.includes('/pages/quiz') ||
          href.includes('#mrf-quiz');
        if (isQuizLink) {
          e.preventDefault();
          Modal.open();
        }
      });
    }

    // Note: backdrop has pointer-events:none so we can't close on outside-click anymore.
    // User closes via the × button or Esc key. Trade-off: site scroll passes through.

    if (!window._mrfQuizEscBound) {
      window._mrfQuizEscBound = true;
      window.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
          const modal = document.querySelector('[data-mrf-quiz-modal][aria-hidden="false"]');
          if (modal) Modal.close();
        }
      });
    }

    // Floating button dismiss
    document.querySelectorAll('[data-floating-dismiss]').forEach(el => {
      if (el._mrfDismissBound) return;
      el._mrfDismissBound = true;
      el.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        const wrap = el.closest('.mrf-quiz-floating');
        if (wrap) {
          wrap.style.display = 'none';
          try {
            document.cookie = `mrf_floating_dismissed=1; max-age=${7 * 24 * 3600}; path=/`;
          } catch (_) {}
        }
      });
    });
  }

  // ============================================================
  // Boot
  // ============================================================

  function boot() {
    // Initialize standalone (on-page) quizzes
    document.querySelectorAll('.mrf-quiz').forEach(el => {
      // Skip the one inside a hidden modal — it inits on open
      const inModal = el.closest('[data-mrf-quiz-modal]');
      if (inModal && inModal.getAttribute('aria-hidden') !== 'false') return;
      if (!el._mrfInstance) el._mrfInstance = new MRFQuiz(el);
    });
    wireTriggers();

    // Auto-open modal if the URL signals "open the quiz" on page load.
    // Two channels:
    //   1. Hash anchor `#mrf-quiz` / `#quiz` / `#open-quiz` — works for in-site links.
    //   2. Query param `?openQuiz=1` / `?quiz=1` — works for email/Klaviyo links
    //      where 302 redirects can drop the URL fragment.
    const hash = (window.location.hash || '').toLowerCase();
    const search = (window.location.search || '').toLowerCase();
    const urlAsksQuiz =
      hash.includes('mrf-quiz') ||
      hash === '#quiz' ||
      hash === '#open-quiz' ||
      /[?&]openquiz=1\b/.test(search) ||
      /[?&]quiz=1\b/.test(search);
    if (urlAsksQuiz) {
      // Slight delay so other DOM-dependent scripts finish first
      setTimeout(() => Modal.open(), 80);
    }

    // Hide floating if dismissed earlier
    if (document.cookie.includes('mrf_floating_dismissed=1')) {
      document.querySelectorAll('.mrf-quiz-floating').forEach(el => el.style.display = 'none');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose for theme editor live preview
  window.MRFQuiz = MRFQuiz;
  window.MRFQuizModal = Modal;
})();
